import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IGoogleAdsKeywordIdea } from '@feathr/blackbox';
import { Label, Skeleton, Text } from '@feathr/components';

import KeywordSuggestion from './KeywordSuggestion';

interface IKeywordSuggestionsProps {
  ideas: IGoogleAdsKeywordIdea[];
  isLoading: boolean;
  hasError: boolean;
  /** Whether suggestions have been asked for, which decides between silence and an empty state. */
  hasFetched: boolean;
  onAdd: (text: string) => void;
  disabled?: boolean;
}

/**
 * Google's suggested keywords for the ad group, as chips that add themselves.
 *
 * Every state renders inline and none of them blocks the field above: suggestions are an
 * aid, so a slow, empty or failed fetch must still leave the user typing keywords by hand.
 * A failure is deliberately not a toast — the user did not cause it and cannot act on it.
 *
 * @param props - {@link IKeywordSuggestionsProps}.
 */
function KeywordSuggestions({
  ideas,
  isLoading,
  hasError,
  hasFetched,
  onAdd,
  disabled = false,
}: Readonly<IKeywordSuggestionsProps>): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  if (isLoading) {
    return <Skeleton />;
  }

  if (hasError) {
    return (
      <Text.DefaultRegular>
        {t("Suggestions aren't available right now. You can still add keywords yourself.")}
      </Text.DefaultRegular>
    );
  }

  // Before the first request there is nothing to report, so the row collapses entirely.
  if (!hasFetched) {
    return null;
  }

  /*
   * Answering an explicit request with an empty area reads as a broken button, so a
   * request that found nothing says so.
   */
  if (!ideas.length) {
    return (
      <Text.DefaultRegular>
        {t('No suggestions for these keywords. Try adding a keyword and asking again.')}
      </Text.DefaultRegular>
    );
  }

  return (
    <>
      <Label weight={'medium'}>{t('Suggested — added as broad match')}</Label>
      {/* Slightly wider than the keyword chips above — this row reads dense enough to need it. */}
      <Flex gap={theme.spacing['1_half']} wrap={'wrap'}>
        {ideas.map((idea) => (
          <KeywordSuggestion disabled={disabled} key={idea.text} onAdd={onAdd} text={idea.text} />
        ))}
      </Flex>
    </>
  );
}

export default KeywordSuggestions;
